import React, { Suspense, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { AppErrorBoundary, BrandLockup } from '@fieldwise/ui/shared';
import type Keycloak from 'keycloak-js';
import { configureSigningAuth } from '@fieldwise/signing';

const FieldWiseApp = React.lazy(() => import('@fieldwise/ui/sender').then((module) => ({ default: module.FieldWiseApp })));
const SignerPortal = React.lazy(() => import('@fieldwise/ui/signer').then((module) => ({ default: module.SignerPortal })));

function signerTokenFromLocation() {
  const queryToken = new URLSearchParams(window.location.search).get('token');
  if (queryToken) return queryToken;
  const match = window.location.pathname.match(/^\/sign\/([A-Za-z0-9_-]{16,})\/?$/);
  return match?.[1] ?? null;
}

const signingToken = signerTokenFromLocation();
const opsHash = new URLSearchParams(window.location.hash.slice(1));
const opsHandoff = opsHash.get('ops_token');
const opsDocumentId = opsHash.get('ops_document');
const opsOrigin = (() => {
  try {
    return new URL(import.meta.env.VITE_FIELDWISE_OPS_URL || 'https://fieldwiseops.io').origin;
  } catch {
    return 'https://fieldwiseops.io';
  }
})();
const allowedSenderEmails = new Set(
  (import.meta.env.VITE_ALLOWED_SIGN_EMAILS || 'rigo@archlabor.co,harrison@archlabor.co,info@archlabor.co,info@fieldwise.io,office@fieldwise.io')
    .split(',')
    .map((email: string) => email.trim().toLowerCase())
    .filter(Boolean),
);

function scrubOpsHandoffCredentials() {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  if (!hash.has('ops_token') && !hash.has('ops_document')) return;
  hash.delete('ops_token');
  hash.delete('ops_document');
  const remainingHash = hash.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}${remainingHash ? `#${remainingHash}` : ''}`,
  );
}

if (opsHandoff) {
  scrubOpsHandoffCredentials();
  window.queueMicrotask(scrubOpsHandoffCredentials);
  window.addEventListener('load', scrubOpsHandoffCredentials, { once: true });
}

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const appBundle = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src || 'current';
    const serviceWorkerUrl = `/sw.js?v=${encodeURIComponent(appBundle.split('/').pop() || 'current')}`;
    const wasControlled = Boolean(navigator.serviceWorker.controller);
    let reloadingForUpdate = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // A recipient may be opening or signing a document when an update lands.
      // Let that session finish; the next navigation will load the new version.
      if (signingToken || !wasControlled || reloadingForUpdate) return;
      reloadingForUpdate = true;
      window.location.reload();
    });
    void navigator.serviceWorker.register(serviceWorkerUrl, { updateViaCache: 'none' }).then((registration) => registration.update());
  });
}

interface AuthIssue {
  code: string;
  message: string;
  browserBlocked: boolean;
}

function isLikelyEmbeddedBrowser() {
  // The hardened Electron shell is an intentional first-party desktop client,
  // not an unknown in-app browser. Let Firebase attempt the normal Workspace
  // popup there and retain the external-browser fallback for other webviews.
  if (window.fieldwiseDesktop) return false;
  return /\b(wv|WebView)\b|Electron|FBAN|FBAV|Instagram|Line\/|Codex/i.test(window.navigator.userAgent);
}

function authIssueFrom(error: unknown): AuthIssue {
  const candidate = error as { code?: string; message?: string };
  const code = candidate?.code || 'auth/unknown-error';
  const browserBlocked = ['auth/internal-error', 'auth/popup-blocked', 'auth/operation-not-supported-in-this-environment', 'auth/web-storage-unsupported'].includes(code);
  const message = candidate?.message || 'Google authentication did not complete.';
  return { code, message, browserBlocked };
}

async function withDeadline<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timer = 0;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => { timer = window.setTimeout(() => reject(new Error(message)), timeoutMs); }),
    ]);
  } finally {
    window.clearTimeout(timer);
  }
}

function SenderApplication() {
  const keycloakRef = useRef<Keycloak | null>(null);
  const authMode = import.meta.env.VITE_AUTH_MODE || '';
  const pinSessionKey = 'fieldwise-sign:pin-session';
  const pinTokenRef = useRef(authMode === 'pin' ? window.localStorage.getItem(pinSessionKey) : null);
  const needsAuthentication = ['oidc', 'firebase', 'pin'].includes(authMode);
  const [state, setState] = useState<'loading' | 'signed-out' | 'ready' | 'denied' | 'error'>(needsAuthentication || opsHandoff ? 'loading' : 'ready');
  const [firebaseSignIn, setFirebaseSignIn] = useState<(() => void) | null>(null);
  const [pinSignIn, setPinSignIn] = useState<((pin: string) => Promise<void>) | null>(null);
  const [authIssue, setAuthIssue] = useState<AuthIssue | null>(null);
  const [embeddedBrowser, setEmbeddedBrowser] = useState(isLikelyEmbeddedBrowser);
  useEffect(() => {
    if (opsHandoff) {
      let cancelled = false;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20_000);
      configureSigningAuth(() => opsHandoff, 'fieldwise-ops');
      const apiUrl = import.meta.env.VITE_SIGNING_API_URL || '/api';
      void fetch(`${apiUrl.replace(/\/$/, '')}/v1/session`, {
        headers: { authorization: `Bearer ${opsHandoff}`, 'x-fieldwise-client': 'fieldwise-ops' },
        cache: 'no-store',
        signal: controller.signal,
      }).then(async (response) => {
        window.clearTimeout(timeout);
        const payload = await response.json().catch(() => ({})) as { email?: string };
        if (!response.ok || !payload.email || !allowedSenderEmails.has(payload.email.toLowerCase())) throw new Error('This FieldWise Ops account is not authorized for Sign.');
        scrubOpsHandoffCredentials();
        if (!cancelled) setState('ready');
      }).catch((error) => {
        window.clearTimeout(timeout);
        console.error('FieldWise Ops sign-in failed', error);
        if (!cancelled) {
          if (controller.signal.aborted) {
            setAuthIssue({ code: 'auth/ops-timeout', message: 'FieldWise Ops did not verify this session in time.', browserBlocked: false });
            setState('error');
          } else setState('denied');
        }
      });
      return () => { cancelled = true; window.clearTimeout(timeout); controller.abort(); };
    }
    if (authMode === 'pin') {
      let cancelled = false;
      const apiUrl = (import.meta.env.VITE_SIGNING_API_URL || '/api').replace(/\/$/, '');
      configureSigningAuth(() => pinTokenRef.current || undefined);
      const clearPinSession = () => {
        pinTokenRef.current = null;
        window.localStorage.removeItem(pinSessionKey);
      };
      const verifyStoredSession = async () => {
        if (!pinTokenRef.current) {
          if (!cancelled) setState('signed-out');
          return;
        }
        const response = await fetch(`${apiUrl}/v1/session`, {
          headers: { authorization: `Bearer ${pinTokenRef.current}` },
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('Your saved PIN session is no longer valid.');
        if (!cancelled) setState('ready');
      };
      setPinSignIn(() => async (pin: string) => {
        setAuthIssue(null);
        setState('loading');
        try {
          const response = await fetch(`${apiUrl}/v1/pin/login`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ pin }),
          });
          const payload = await response.json().catch(() => ({})) as { token?: string; error?: string };
          if (!response.ok || !payload.token) throw Object.assign(new Error(payload.error || 'PIN sign-in did not complete.'), { status: response.status });
          pinTokenRef.current = payload.token;
          window.localStorage.setItem(pinSessionKey, payload.token);
          configureSigningAuth(() => pinTokenRef.current || undefined);
          if (!cancelled) setState('ready');
        } catch (error) {
          clearPinSession();
          const candidate = error as { message?: string; status?: number };
          if (!cancelled) {
            setAuthIssue({
              code: candidate.status === 429 ? 'auth/too-many-attempts' : 'auth/invalid-pin',
              message: candidate.message || 'PIN sign-in did not complete.',
              browserBlocked: false,
            });
            setState('signed-out');
          }
        }
      });
      void verifyStoredSession().catch(() => {
        clearPinSession();
        if (!cancelled) setState('signed-out');
      });
      return () => { cancelled = true; };
    }
    if (import.meta.env.VITE_AUTH_MODE === 'firebase') {
      let unsubscribe = () => {};
      let cancelled = false;
      const initializationTimeout = window.setTimeout(() => {
        if (cancelled) return;
        setAuthIssue({ code: 'auth/initialization-timeout', message: 'The authentication service did not initialize in time.', browserBlocked: false });
        setState('error');
      }, 20_000);
      const firebaseConfig = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
      };
      void Promise.all([import('firebase/app'), import('firebase/auth')]).then(([firebaseApp, firebaseAuth]) => {
        if (cancelled) return;
        const firebase = firebaseApp.getApps().length ? firebaseApp.getApp() : firebaseApp.initializeApp(firebaseConfig);
        const auth = firebaseAuth.getAuth(firebase);
        const provider = new firebaseAuth.GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        const userIsAllowed = (email?: string | null) => Boolean(email && allowedSenderEmails.has(email.toLowerCase()));
        configureSigningAuth(() => auth.currentUser?.getIdToken());
        return firebaseAuth.setPersistence(auth, firebaseAuth.browserLocalPersistence).then(() => {
          if (cancelled) return;
          unsubscribe = firebaseAuth.onAuthStateChanged(auth, (user) => {
            window.clearTimeout(initializationTimeout);
            if (user && userIsAllowed(user.email)) setState('ready');
            else if (user) {
              void auth.signOut();
              setState('denied');
            }
            else setState('signed-out');
          });
          setFirebaseSignIn(() => () => {
            setAuthIssue(null);
            setState('loading');
            void withDeadline(firebaseAuth.signInWithPopup(auth, provider), 90_000, 'Google sign-in did not finish in time. Close any open sign-in window and try again.').then(async (result) => {
              if (userIsAllowed(result.user.email)) {
                setAuthIssue(null);
                setState('ready');
              }
              else {
                await auth.signOut();
                setState('denied');
              }
            }).catch((error) => {
              const issue = authIssueFrom(error);
              console.error('Firebase Google sign-in failed', error);
              setAuthIssue(issue);
              setEmbeddedBrowser((current) => current || issue.browserBlocked);
              setState('signed-out');
            });
          });
        });
      }).catch((error) => {
        window.clearTimeout(initializationTimeout);
        setAuthIssue(authIssueFrom(error));
        console.error('Firebase persistence could not be initialized', error);
        if (!cancelled) setState('error');
      });
      return () => {
        cancelled = true;
        window.clearTimeout(initializationTimeout);
        unsubscribe();
      };
    }
    if (import.meta.env.VITE_AUTH_MODE !== 'oidc') return;
    let cancelled = false;
    void import('keycloak-js').then(({ default: KeycloakClient }) => {
      if (cancelled) return;
      const keycloak = new KeycloakClient({
        url: import.meta.env.VITE_KEYCLOAK_URL || 'http://127.0.0.1:8080',
        realm: import.meta.env.VITE_KEYCLOAK_REALM || 'fieldwise',
        clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'fieldwise-sign',
      });
      keycloakRef.current = keycloak;
      configureSigningAuth(async () => { await keycloak.updateToken(30); return keycloak.token; });
      return withDeadline(keycloak.init({ onLoad: 'login-required', pkceMethod: 'S256', checkLoginIframe: false }), 30_000, 'The secure sign-in service did not respond in time.').then((authenticated) => setState(authenticated ? 'ready' : 'error'));
    }).catch((error) => { if (!cancelled) { setAuthIssue(authIssueFrom(error)); setState('error'); } });
    return () => { cancelled = true; keycloakRef.current = null; };
  }, []);
  const lockWorkspace = authMode === 'pin' ? () => {
    pinTokenRef.current = null;
    window.localStorage.removeItem(pinSessionKey);
    configureSigningAuth(() => undefined);
    setAuthIssue(null);
    setState('signed-out');
  } : undefined;
  if (state !== 'ready') return <SignAccessScreen state={state} authIssue={authIssue} embeddedBrowser={embeddedBrowser} pinMode={authMode === 'pin'} onPinSignIn={(pin) => pinSignIn?.(pin) ?? Promise.resolve()} onPopupSignIn={() => firebaseSignIn?.()} />;
  return (
    <FieldWiseApp
      onLockWorkspace={opsHandoff ? undefined : lockWorkspace}
      opsDocumentHandoff={opsHandoff && opsDocumentId
        ? { id: opsDocumentId, sourceOrigin: opsOrigin }
        : undefined}
    />
  );
}

function SignAccessScreen({ state, authIssue, embeddedBrowser, pinMode, onPinSignIn, onPopupSignIn }: { state: 'loading' | 'signed-out' | 'denied' | 'error'; authIssue: AuthIssue | null; embeddedBrowser: boolean; pinMode: boolean; onPinSignIn: (pin: string) => Promise<void>; onPopupSignIn: () => void }) {
  const [addressCopied, setAddressCopied] = useState(false);
  const [pin, setPin] = useState('');
  const copy = pinMode
    ? { eyebrow: 'Private workspace access', title: 'Enter your access PIN.', description: 'Use your four-digit PIN to open your FieldWise Sign account.' }
    : state === 'denied'
    ? { eyebrow: 'Private beta access', title: 'Access not authorized.', description: 'This Google account is not on the approved FieldWise Sign access list.' }
    : state === 'error'
      ? { eyebrow: 'Secure workspace access', title: 'Sign-in unavailable.', description: 'The authentication service could not start. Refresh the page and try again.' }
      : { eyebrow: 'Welcome to your workspace', title: 'Welcome back.', description: 'Sign in to prepare, send, and track documents from one secure workspace.' };

  return (
    <main className="sign-auth-shell">
      <section className="sign-auth-story">
        <div className="sign-auth-orbit sign-auth-orbit-one" />
        <div className="sign-auth-orbit sign-auth-orbit-two" />
        <header>
          <BrandLockup />
          <span>Private document workspace</span>
        </header>
        <div className="sign-auth-story-copy">
          <span>Documents that keep work moving</span>
          <h1>Prepare.<br />Send.<br /><em>Signed.</em></h1>
          <p>Create, edit, route, and complete documents in one focused workspace—built for teams that need a clear path from PDF to signature.</p>
        </div>
        <div className="sign-auth-assurances">
          <article>
            <i aria-hidden="true">01</i>
            <strong>Controlled access</strong>
            <small>Approved access only</small>
          </article>
          <article>
            <i aria-hidden="true">02</i>
            <strong>Complete visibility</strong>
            <small>Sending and signing activity tracked</small>
          </article>
        </div>
      </section>

      <section className="sign-auth-canvas">
        <div className="sign-auth-mobile-brand"><BrandLockup /><span>Private document workspace</span></div>
        <div className="sign-auth-heading">
          <span>{copy.eyebrow}</span>
          <h2>{copy.title}</h2>
          <p>{copy.description}</p>
        </div>
        <div className="sign-auth-card">
          <div className="sign-auth-card-heading">
            <strong>Secure workspace access</strong>
            <span>{pinMode ? 'Enter your four-digit PIN. Account names are never shown on this screen.' : 'Use an approved FieldWise or Arch Labor Google Workspace account.'}</span>
          </div>
          {state === 'loading' ? (
            <div className="sign-auth-progress" role="status"><span className="loading-spinner" /><strong>Checking access…</strong></div>
          ) : state === 'error' ? (
            <button className="sign-auth-google" type="button" onClick={() => window.location.reload()}>
              {state === 'error' ? <span aria-hidden="true">↻</span> : <span className="google-g" aria-hidden="true">G</span>}
              Refresh and try again
            </button>
          ) : pinMode ? (
            <form className="sign-auth-pin" onSubmit={(event) => { event.preventDefault(); if (pin.length === 4) void onPinSignIn(pin); }}>
              <label htmlFor="fieldwise-access-pin">Access PIN</label>
              <input id="fieldwise-access-pin" autoFocus type="password" inputMode="numeric" autoComplete="current-password" pattern="[0-9]{4}" maxLength={4} value={pin} onChange={(event) => { setPin(event.target.value.replace(/\D/g, '').slice(0, 4)); }} aria-describedby={authIssue ? 'fieldwise-pin-error' : undefined} />
              <button className="sign-auth-google" type="submit" disabled={pin.length !== 4}>Open workspace</button>
            </form>
          ) : embeddedBrowser ? (
            <div className="sign-auth-browser-options">
              <a className="sign-auth-external-primary" href={`${window.location.origin}/`} target="_blank" rel="noreferrer"><span className="google-g" aria-hidden="true">G</span>Open Sign in Chrome or Safari <span aria-hidden="true">↗</span></a>
              <button className="sign-auth-copy-link" type="button" onClick={() => void window.navigator.clipboard.writeText(`${window.location.origin}/`).then(() => setAddressCopied(true)).catch(() => setAddressCopied(false))}>{addressCopied ? 'Website address copied' : 'Copy sign.fieldwise.io'}</button>
            </div>
          ) : (
            <button className="sign-auth-google" type="button" onClick={onPopupSignIn}>
              <span className="google-g" aria-hidden="true">G</span>
              {state === 'denied' ? 'Try another Google account' : 'Continue with Google Workspace'}
            </button>
          )}
          {authIssue && (
            <div id={pinMode ? 'fieldwise-pin-error' : undefined} className="sign-auth-error-detail" role="alert">
              <strong>{pinMode ? authIssue.code === 'auth/too-many-attempts' ? 'Too many PIN attempts. Wait 15 minutes and try again.' : 'That PIN was not recognized.' : authIssue.code === 'auth/popup-blocked' ? 'This browser blocked the secure Google sign-in window.' : authIssue.browserBlocked ? 'Google sign-in could not open in this browser.' : 'Google sign-in did not complete.'}</strong>
              <span>{authIssue.message}</span>
              {!pinMode && <code>{authIssue.code}</code>}
            </div>
          )}
          {state === 'denied' && <p className="sign-auth-alert">Contact your FieldWise administrator if this account should have access.</p>}
          <div className="sign-auth-private-note">
            <strong>Private FieldWise workspace</strong>
            <span>Recipient signing links do not require a FieldWise account.</span>
          </div>
        </div>
        <footer>FieldWise Sign · Documents ready. Work moving.</footer>
      </section>
    </main>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <Suspense fallback={<main className="portal-centered"><span className="loading-spinner" /><strong>Opening FieldWise Sign…</strong><div className="portal-loading-help"><span>Taking longer than expected? Refresh the page and try again.</span><button type="button" onClick={() => window.location.reload()}>Refresh page</button></div></main>}>
        {signingToken ? <SignerPortal token={signingToken} /> : <SenderApplication />}
      </Suspense>
    </AppErrorBoundary>
  </React.StrictMode>,
);
